import React, { useState, useEffect, useMemo } from "react";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import {
  getVendedoresComisiones,
  postComisionesComercialAnualMensual,
} from "../../../Services/fetchComisiones";
import "./comisiones.css";
import Button from "../../Elements/Button/Button";

export default function ComisionesComercial() {
  const showToast = useToast();

  // — Lista de vendedores —
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [vendorsError, setVendorsError] = useState(null);
  const [comercialId, setComercialId] = useState("");
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [fechaInicio, setFechaInicio] = useState(`${new Date().getFullYear()}-01-01`);
  const [fechaFin, setFechaFin] = useState(`${new Date().getFullYear()}-12-31`);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const renderErrorText = (err) =>
    typeof err === "string" ? err : err?.message || String(err);

  // 1) Load vendedores
  useEffect(() => {
    setVendorsLoading(true);
    getVendedoresComisiones()
      .then(({ code, data }) => {
        if (code === 200) {
          setVendors(data);
          if (data.length > 0) setComercialId(data[0].eId.toString());
        } else throw new Error("Error al cargar vendedores");
      })
      .catch((err) => setVendorsError(renderErrorText(err)))
      .finally(() => setVendorsLoading(false));
  }, []);

  // 2) Ajustar fechas al cambiar el año
  useEffect(() => {
    const y = Number(anio);
    setFechaInicio(`${y}-01-01`);
    setFechaFin(`${y}-12-31`);
  }, [anio]);

  // 3) Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setData([]);
    showToast("info", "Consultando comisiones...");

    try {
      let result;
      if (comercialId === "all") {
        const allResponses = await Promise.all(
          vendors.map((v) =>
            postComisionesComercialAnualMensual({
              comercial_id: v.eId,
              anio: Number(anio),
              fechaInicio,
              fechaFin,
            }).then(({ data }) => data)
          )
        );
        const flat = allResponses.flat();
        const grouped = {};
        flat.forEach(({ periodo, comision_total, anio: yr }) => {
          const key = periodo;
          const monto = Number(comision_total) || 0;
          if (!grouped[key]) grouped[key] = { periodo, comision_total: 0, anio: yr };
          grouped[key].comision_total += monto;
        });
        result = Object.values(grouped).map(({ periodo, comision_total, anio: yr }) => ({
          periodo,
          comercial_nombre: "Todos",
          comision_total: Number(comision_total.toFixed(2)),
          anio: yr,
        }));
      } else {
        const { code, data } = await postComisionesComercialAnualMensual({
          comercial_id: Number(comercialId),
          anio: Number(anio),
          fechaInicio,
          fechaFin,
        });
        if (code !== 200) throw new Error("Error al calcular comisiones");
        result = data;
      }

      setData(result);
      showToast("success", "Comisiones cargadas correctamente");
    } catch (err) {
      const msg = renderErrorText(err);
      setError(msg);
      showToast("error", msg);
    } finally {
      setLoading(false);
    }
  };

  // Totales / resumen
  const totalComisiones = useMemo(
    () =>
      data.reduce((sum, r) => sum + (Number(r.comision_total) || 0), 0),
    [data]
  );
  const periodosCount = data.length;

  // CSV export rápido
  const exportCSV = () => {
    if (data.length === 0) return;
    const headers = ["Periodo", "Comercial", "Comisión Total", "Año"];
    const rows = data.map((d) => [
      d.periodo,
      d.comercial_nombre,
      d.comision_total,
      d.anio,
    ]);
    const csv = [headers, ...rows]
      .map((r) =>
        r
          .map((c) => {
            const v = c == null ? "" : c.toString().replace(/"/g, '""');
            return `"${v}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comisiones_${comercialId}_${anio}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="com-wrapper container-mh">
      <h2 className="com-title">Comisiones por Comercial</h2>

      {/* Vendedores loading/error */}
      {vendorsLoading && <p className="text-muted">Cargando vendedores…</p>}
      {!vendorsLoading && vendorsError && (
        <div className="alert alert-danger">
          Error al cargar vendedores: {vendorsError}
        </div>
      )}

      {/* Form */}
      {!vendorsLoading && !vendorsError && (
        <form onSubmit={handleSubmit} className="com-form card-glass mb-4">
          <div className="row g-3 align-items-end">
            <div className="col-lg-4">
              <label className="form-label">Comercial</label>
              <select
                className="form-select"
                value={comercialId}
                onChange={(e) => setComercialId(e.target.value)}
                required
              >
                <option value="all">Todos</option>
                {vendors.flatMap(({ nombre, eId, gId }) => [
                  <option key={`e-${eId}`} value={eId}>
                    {`${nombre} (${eId})`}
                  </option>,
                  <option key={`g-${gId}`} value={gId}>
                    {`${nombre} (${gId})`}
                  </option>,
                ])}
              </select>
            </div>

            <div className="col-sm-3 col-lg-2">
              <label className="form-label">Año</label>
              <input
                type="number"
                className="form-control"
                value={anio}
                onChange={(e) => setAnio(e.target.value)}
                min={2000}
                max={2100}
                required
              />
            </div>

            <div className="col-sm-2 col-lg-1">
              <Button
                text="Buscar"
                loadingText="..."
                isLoading={loading}
                type="submit"
                fill
                color="blue"
                classNames="w-100"
                disabled={loading}
              />
            </div>
          </div>
        </form>
      )}

      {/* Error post consulta */}
      {!loading && error && (
        <div className="alert alert-danger">Error: {error}</div>
      )}

      {/* Resumen */}
      {!loading && !error && data.length > 0 && (
        <div className="com-summary mb-3">
          <div className="com-summary-item">
            <span className="label">Total comisión</span>
            <span className="value">
              {totalComisiones.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="com-summary-item">
            <span className="label">Periodos</span>
            <span className="value">{periodosCount}</span>
          </div>
          <div className="com-summary-item">
            <span className="label">Rango</span>
            <span className="value">
              {fechaInicio} &rarr; {fechaFin}
            </span>
          </div>
          <Button
            text="Exportar CSV"
            action={exportCSV}
            type="button"
            color="secondary"      // o el que uses: "gray", "blue", etc.
            fill={false}           // outline
            classNames="ms-auto btn-sm"
            padding                // o false si quieres el grande
          />
        </div>
      )}

      {/* Tabla */}
      {!loading && data.length > 0 && (
        <div className="table-responsive com-table-wrapper">
          <table className="table com-table">
            <thead>
              <tr>
                <th>Periodo</th>
                <th>Comercial</th>
                <th>Comisión Total</th>
                <th>Año</th>
              </tr>
            </thead>
            <tbody>
              {data.map(({ periodo, comercial_nombre, comision_total, anio: yr }, i) => (
                <tr key={i}>
                  <td data-label="Periodo">{periodo.replace("_", " ").toUpperCase()}</td>
                  <td data-label="Comercial">{comercial_nombre}</td>
                  <td data-label="Comisión Total" className="text-end fw-semibold">
                    {comision_total != null
                      ? Number(comision_total).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                      })
                      : "-"}
                  </td>
                  <td data-label="Año">{yr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && data.length === 0 && (
        <p className="text-muted text-center mt-4">
          No hay resultados. Completa el formulario y presiona “Buscar”.
        </p>
      )}
    </div>
  );
}
